import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import MessageService from '../services/MessageService';
import {
  cacheConversation,
  cacheConversations,
  cacheMessages,
  getCachedConversation,
  getCachedConversations,
  getCachedMessages,
  removeCachedConversation,
  upsertMessage,
} from '../services/MessagesCacheDb';
import SoundManager from '../utils/SoundManager';

const MessagesContext = createContext();

export const MessageStatus = {
  SENT: 'sent',
  DELIVERED: 'delivered',
  SEEN: 'seen'
};

export const useMessages = () => {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessages must be used within a MessagesProvider');
  }
  return context;
};

export const MessagesProvider = ({ children, user, messagesSocketService }) => {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [conversationUnreadCount, setConversationUnreadCount] = useState({});
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [error, setError] = useState(null);

  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [conversationsPage, setConversationsPage] = useState(1);

  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [messagesPage, setMessagesPage] = useState(1);
  const limit = 20;
  const userId = user?.id;

  const conversationsRef = useRef(conversations);
  const messagesRef = useRef(messages);
  const selectedConversationIdRef = useRef(null);
  const selectConversationRequestIdRef = useRef(0);
  const selectConversationAbortRef = useRef(null);

  const generateClientId = useCallback(() => {
    return `client-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }, []);

  const getUserAvatarUrlFromConversation = useCallback((conversation, targetUserId) => {
    if (!conversation || !targetUserId) return null;
    const participant = (conversation.participants || []).find((p) => {
      const pid = p?.user?.id || p?.participantId || p?.id;
      return pid === targetUserId;
    });
    const media = participant?.user?.profile?.media;

    if (participant?.user?.profile?.current_avatar) {
      return participant.user.profile.current_avatar.url;
    }
    if (Array.isArray(media)) {
      const avatar = media.reverse().find((m) => m.type === 'avatar');
      return avatar?.url || null;
    }
    return participant?.user?.profile?.avatar || null;
  }, []);

  const normalizeMessage = useCallback((msg, fallback = {}) => {
    if (!msg) return null;
    const merged = { ...fallback, ...(msg.data || msg) };

    // Prefer current avatar if available (socket payloads may include stale profile.avatar)
    if (merged?.sender?.profile?.currentAvatar?.url) {
      merged.sender = {
        ...merged.sender,
        profile: {
          ...(merged.sender.profile || {}),
          avatar: merged.sender.profile.currentAvatar.url,
        },
      };
    }
    return {
      id: merged.id || merged.messageId,
      conversationId: merged.conversationId || fallback.conversationId,
      content: merged.content ?? fallback.content,
      type: merged.type || fallback.type || 'text',
      metadata: merged.metadata || fallback.metadata,
      replyTo: merged.replyTo || fallback.replyTo || null,
      sender: merged.sender || fallback.sender,
      recipients: merged.recipients || fallback.recipients || [],
      reactions: merged.reactions || fallback.reactions || [],
      status: merged.status || fallback.status,
      pending: Boolean(merged.pending || fallback.pending),
      clientId: merged.clientId || fallback.clientId,
      createdAt: merged.createdAt || fallback.createdAt || new Date().toISOString(),
      updatedAt: merged.updatedAt || fallback.updatedAt || merged.createdAt || fallback.createdAt,
      ...merged,
    };
  }, []);

  const upsertMessages = useCallback((prevMessages, nextMessage) => {
    if (!nextMessage) return prevMessages;
    const nextId = nextMessage.id;
    const nextClientId = nextMessage.clientId;

    if (nextId) {
      const idx = prevMessages.findIndex((m) => m?.id === nextId);
      if (idx >= 0) {
        const copy = prevMessages.slice();
        copy[idx] = { ...copy[idx], ...nextMessage, pending: Boolean(nextMessage.pending) };
        return copy;
      }
    }

    if (nextClientId) {
      const idx = prevMessages.findIndex((m) => m?.clientId && m.clientId === nextClientId);
      if (idx >= 0) {
        const copy = prevMessages.slice();
        copy[idx] = { ...prevMessages[idx], ...nextMessage, pending: Boolean(nextMessage.pending) };
        return copy;
      }
    }

    // Avoid accidental duplicates if we already have the same (id-less) optimistic message
    if (!nextId && nextClientId) {
      if (prevMessages.some((m) => m?.clientId === nextClientId)) return prevMessages;
    }

    return [...prevMessages, nextMessage];
  }, []);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    return () => {
      try {
        selectConversationAbortRef.current?.abort?.();
      } catch {
      }
    };
  }, []);

  // Compute unread counts
  const computeConversationUnreadCount = useCallback((conversations) => {
    if (!conversations.length) return 0;

    conversations.forEach(conversation => {
      setConversationUnreadCount(prev => ({
        ...prev,
        [conversation.id]: conversation?.stats?.unread || 0
      }));
    });
  }, []);

  const safeAsync = useCallback((fn) => {
    Promise.resolve()
      .then(fn)
      .catch(() => {
      });
  }, []);

  useEffect(() => {
    if (!userId) return;

    safeAsync(async () => {
      const cached = await getCachedConversations(userId);
      if (Array.isArray(cached) && cached.length > 0) {
        computeConversationUnreadCount(cached);
        setConversations(cached);
      }
    });
  }, [userId, safeAsync, computeConversationUnreadCount]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { conversations: fetchedConversations, total, page: currentPage, totalPages } = 
        await MessageService.getConversations(1, limit);

        console.log("COnversations: ", fetchedConversations)

      if (Array.isArray(fetchedConversations)) {
        computeConversationUnreadCount(fetchedConversations);
        setConversations(fetchedConversations);
        setConversationsPage(currentPage);
        setHasMoreConversations(currentPage < totalPages);

        safeAsync(async () => {
          await cacheConversations(userId, fetchedConversations);
        });
      } else {
        setConversations([]);
        setHasMoreConversations(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to load conversations');
      if (conversations.length === 0) {
        setHasMoreConversations(false);
      }
    } finally {
      setLoading(false);
    }
  }, [limit, computeConversationUnreadCount, userId, safeAsync, conversations.length]);

  const fetchMoreConversations = useCallback(async () => {
    if (loadingMoreConversations) return;
    if (!hasMoreConversations) return;

    try {
      setLoadingMoreConversations(true);
      setError(null);

      const nextPage = (conversationsPage || 1) + 1;
      const { conversations: fetchedConversations, page: currentPage, totalPages } = await MessageService.getConversations(
        nextPage,
        limit,
      );

      const nextList = Array.isArray(fetchedConversations) ? fetchedConversations : [];
      setConversations((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        const seen = new Set(base.map((c) => c?.id).filter(Boolean));
        const merged = base.slice();
        nextList.forEach((c) => {
          if (!c?.id) return;
          if (seen.has(c.id)) return;
          seen.add(c.id);
          merged.push(c);
        });
        return merged;
      });

      computeConversationUnreadCount(nextList);
      setConversationsPage(currentPage || nextPage);
      setHasMoreConversations(Boolean((currentPage || nextPage) < (totalPages || 0)));

      safeAsync(async () => {
        const merged = (conversationsRef.current || []).slice();
        const seen = new Set(merged.map((c) => c?.id).filter(Boolean));
        nextList.forEach((c) => {
          if (!c?.id) return;
          if (seen.has(c.id)) return;
          seen.add(c.id);
          merged.push(c);
        });
        await cacheConversations(userId, merged);
      });
    } catch (err) {
      setError(err.message || 'Failed to load more conversations');
      setHasMoreConversations(false);
    } finally {
      setLoadingMoreConversations(false);
    }
  }, [loadingMoreConversations, hasMoreConversations, conversationsPage, limit, computeConversationUnreadCount, safeAsync, userId]);

  // Update total unread count
  useEffect(() => {
    setTotalUnreadCount(Object.values(conversationUnreadCount).reduce((a, b) => a + b, 0));
  }, [conversationUnreadCount]);

  // Select a conversation and fetch its messages
  const selectConversation = useCallback(async (conversationId) => {
    if (!conversationId) {
      setError('No conversation selected');
      return;
    }

    try {
      selectedConversationIdRef.current = conversationId;

      setConversationUnreadCount((prev) => ({
        ...(prev || {}),
        [conversationId]: 0,
      }));

      setConversations((prev) =>
        (Array.isArray(prev) ? prev : []).map((conv) => {
          if (conv?.id !== conversationId) return conv;
          return {
            ...conv,
            stats: {
              ...(conv?.stats || {}),
              unread: 0,
            },
          };
        }),
      );

      safeAsync(async () => {
        try {
          await MessageService.markAllAsRead(conversationId);
        } catch {
        }
      });

      selectConversationRequestIdRef.current += 1;
      const requestId = selectConversationRequestIdRef.current;
      try {
        selectConversationAbortRef.current?.abort?.();
      } catch {
      }
      const controller = new AbortController();
      selectConversationAbortRef.current = controller;

      setLoading(true);
      setError(null);
      
      // Clear messages from previous conversation to prevent showing stale data
      setMessages([]);

      const cachedConversation = userId ? await getCachedConversation(userId, conversationId) : null;
      const cachedMessages = userId ? await getCachedMessages(userId, conversationId) : [];
      const hasCached = Boolean(cachedConversation) || (Array.isArray(cachedMessages) && cachedMessages.length > 0);
      
      const conversation = conversations.find(c => c.id === conversationId) || 
                          { id: conversationId };

      if (hasCached) {
        const conversationShape = {
          ...conversation,
          ...(cachedConversation || {}),
          currentUserId: userId,
        };

        if (selectedConversationIdRef.current === conversationId && selectConversationRequestIdRef.current === requestId) {
          setCurrentConversation({
            ...conversationShape,
            messages: cachedMessages || [],
          });
          setMessages((prev) => {
            const base = Array.isArray(cachedMessages) ? cachedMessages.map((m) => normalizeMessage(m, { conversationId })) : [];
            return base;
          });
        }
      }
      
      const conversationData = await MessageService.getConversation(conversationId, 1, 50, { signal: controller.signal });

      if (selectedConversationIdRef.current !== conversationId || selectConversationRequestIdRef.current !== requestId) {
        return;
      }
      
      const normalizedServerMessages = Array.isArray(conversationData.messages)
        ? conversationData.messages.map((m) => normalizeMessage(m, { conversationId }))
        : [];

      setCurrentConversation({
        ...conversation,
        ...conversationData,
        messages: normalizedServerMessages,
        currentUserId: user?.id
      });

      // Load pinned messages for this conversation (non-blocking)
      safeAsync(async () => {
        try {
          const pinsResp = await MessageService.getPinnedMessages(conversationId);
          const pins = Array.isArray(pinsResp?.pinned) ? pinsResp.pinned : [];
          if (selectedConversationIdRef.current === conversationId && selectConversationRequestIdRef.current === requestId) {
            setPinnedMessages(pins);
          }
        } catch {
          if (selectedConversationIdRef.current === conversationId && selectConversationRequestIdRef.current === requestId) {
            setPinnedMessages([]);
          }
        }
      });
      
      // Replace messages with server data (don't merge with previous conversation's messages)
      setMessages(normalizedServerMessages);
      const serverPage = Number(conversationData.page || 1);
      const serverTotalPages = Number(conversationData.totalPages || 1);
      setHasMoreMessages(Boolean(serverPage < serverTotalPages));
      setMessagesPage(serverPage);

      console.log("conversationData", conversationData);

      safeAsync(async () => {
        await cacheConversation(userId, {
          ...conversation,
          ...conversationData,
          messages: undefined,
          currentUserId: userId,
        });
        await cacheMessages(userId, conversationId, conversationData.messages || []);
      });

      // Join conversation via socket if available
      if (messagesSocketService) {
        try {
          await messagesSocketService.joinConversation(conversationId);
        } catch (socketError) {
          console.warn('Socket connection failed, but messages loaded:', socketError);
          // Don't fail the whole operation if socket fails
        }
      }

    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return;
      }
      setError(err.message || 'Failed to load conversation');
      if (messages.length === 0) {
        setHasMoreMessages(false);
      }
    } finally {
      if (selectedConversationIdRef.current === conversationId) {
        setLoading(false);
      }
    }
  }, [conversations, messagesSocketService, user?.id, userId, safeAsync, messages.length]);

  const fetchOlderMessages = useCallback(async (conversationId) => {
    const targetConversationId = conversationId || currentConversation?.id;
    if (!targetConversationId) return;
    if (loadingOlderMessages) return;
    if (!hasMoreMessages) return;

    try {
      setLoadingOlderMessages(true);
      setError(null);

      const nextPage = (messagesPage || 1) + 1;
      const conversationData = await MessageService.getConversation(targetConversationId, nextPage, 50);
      const normalizedServerMessages = Array.isArray(conversationData.messages)
        ? conversationData.messages.map((m) => normalizeMessage(m, { conversationId: targetConversationId }))
        : [];

      setMessages((prev) => {
        const base = Array.isArray(prev) ? prev.slice() : [];
        const seen = new Set(base.map((m) => m?.id).filter(Boolean));
        const merged = base.slice();
        normalizedServerMessages.forEach((m) => {
          if (!m) return;
          if (m?.id && seen.has(m.id)) return;
          if (m?.id) seen.add(m.id);
          merged.push(m);
        });
        return merged;
      });

      if (selectedConversationIdRef.current === targetConversationId) {
        setCurrentConversation((prev) => {
          if (!prev) return prev;
          const prevMessages = Array.isArray(prev.messages) ? prev.messages : [];
          const seen = new Set(prevMessages.map((m) => m?.id).filter(Boolean));
          const merged = prevMessages.slice();
          normalizedServerMessages.forEach((m) => {
            if (!m) return;
            if (m?.id && seen.has(m.id)) return;
            if (m?.id) seen.add(m.id);
            merged.push(m);
          });
          return {
            ...prev,
            messages: merged,
          };
        });
      }

      const serverPage = Number(conversationData.page || nextPage);
      const serverTotalPages = Number(conversationData.totalPages || serverPage);
      setHasMoreMessages(Boolean(serverPage < serverTotalPages));
      setMessagesPage(serverPage);

      safeAsync(async () => {
        await cacheMessages(userId, targetConversationId, messagesRef.current || []);
      });
    } catch (err) {
      setError(err.message || 'Failed to load older messages');
      setHasMoreMessages(false);
      throw err;
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [currentConversation?.id, hasMoreMessages, loadingOlderMessages, messagesPage, normalizeMessage, safeAsync, userId]);

  const refreshPinnedMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      const pinsResp = await MessageService.getPinnedMessages(conversationId);
      const pins = Array.isArray(pinsResp?.pinned) ? pinsResp.pinned : [];
      if (selectedConversationIdRef.current === conversationId) {
        setPinnedMessages(pins);
      }
    } catch (err) {
      if (selectedConversationIdRef.current === conversationId) {
        setPinnedMessages([]);
      }
      throw err;
    }
  }, []);

  const pinMessage = useCallback(async (conversationId, messageId) => {
    if (!conversationId || !messageId) return;
    const optimisticPin = {
      id: `optimistic-${conversationId}-${messageId}`,
      conversationId,
      messageId,
      pinnedAt: new Date().toISOString(),
      pinnedBy: user ? { id: user.id, username: user.username, profile: user.profile } : null,
      message: messagesRef.current?.find?.((m) => m?.id === messageId) || null,
      optimistic: true,
    };

    setPinnedMessages((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.some((p) => p?.messageId === messageId)) return list;
      return [optimisticPin, ...list];
    });

    try {
      await MessageService.pinMessage(conversationId, messageId);
      // Backend will create and broadcast the system message
    } catch (err) {
      setPinnedMessages((prev) => (Array.isArray(prev) ? prev.filter((p) => p?.messageId !== messageId) : prev));
      throw err;
    } finally {
      // Always reconcile with server state
      try {
        await refreshPinnedMessages(conversationId);
      } catch (_) {}
    }
  }, [refreshPinnedMessages]);

  const unpinMessage = useCallback(async (conversationId, messageId) => {
    if (!conversationId || !messageId) return;
    let snapshot = null;
    setPinnedMessages((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      snapshot = list;
      return list.filter((p) => p?.messageId !== messageId);
    });

    try {
      await MessageService.unpinMessage(conversationId, messageId);
      // Backend will create and broadcast the system message
    } catch (err) {
      if (snapshot) setPinnedMessages(snapshot);
      throw err;
    } finally {
      try {
        await refreshPinnedMessages(conversationId);
      } catch (_) {}
    }
  }, [refreshPinnedMessages]);

  // Send a new message
  const sendMessage = useCallback(async (content, type = 'text', metadata = {}, replyToId = null) => {
    if (!currentConversation) return null;

    const clientId = generateClientId();
    const optimistic = normalizeMessage(
      {
        id: clientId,
        clientId,
        pending: true,
        status: 'sending',
        conversationId: currentConversation.id,
        type,
        content,
        metadata: Object.keys(metadata || {}).length > 0 ? metadata : undefined,
        replyTo: replyToId ? { id: replyToId } : null,
        sender: user ? { id: user.id, username: user.username, profile: user.profile } : undefined,
        recipients: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { conversationId: currentConversation.id },
    );

    setMessages((prev) => upsertMessages(prev, optimistic));
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === currentConversation.id
          ? {
              ...conv,
              lastMessage: optimistic,
              lastMessageAt: optimistic.createdAt,
              unreadCount: 0,
            }
          : conv,
      ),
    );
    setCurrentConversation((prev) =>
      prev
        ? {
            ...prev,
            lastMessage: optimistic,
            lastMessageAt: optimistic.createdAt,
          }
        : prev,
    );

    try {
      const newMessage = await MessageService.sendMessage(
        currentConversation.id,
        content,
        type,
        metadata,
        replyToId
      );

      const messageToAdd = normalizeMessage(newMessage, {
        clientId,
        pending: false,
        status: 'sent',
        conversationId: currentConversation.id,
        type,
        content,
        metadata,
        sender: user ? { id: user.id, username: user.username, profile: user.profile } : undefined,
      });

      setMessages((prev) => {
        const withoutServerDup = messageToAdd?.id ? prev.filter((m) => m?.id !== messageToAdd.id) : prev;
        return upsertMessages(withoutServerDup, { ...messageToAdd, pending: false, clientId });
      });
      
      setConversations(prev => 
        prev.map(conv => 
          conv.id === currentConversation.id 
            ? { 
                ...conv, 
                lastMessage: messageToAdd,
                lastMessageAt: messageToAdd.createdAt || new Date().toISOString(),
                unreadCount: 0
              }
            : conv
        )
      );
      
      setCurrentConversation(prev => ({
        ...prev,
        lastMessage: messageToAdd,
        lastMessageAt: messageToAdd.createdAt || new Date().toISOString()
      }));

      safeAsync(async () => {
        await upsertMessage(userId, currentConversation.id, messageToAdd);
        await cacheConversation(userId, {
          ...(currentConversation || {}),
          lastMessage: messageToAdd,
          lastMessageAt: messageToAdd.createdAt || new Date().toISOString(),
          currentUserId: userId,
        });
      });
      
      return newMessage;
    } catch (err) {
      setError(err.message);
      setMessages((prev) =>
        prev.map((m) => (m?.clientId === clientId || m?.id === clientId ? { ...m, pending: false, status: 'failed' } : m)),
      );
      throw err;
    }
  }, [currentConversation, generateClientId, normalizeMessage, safeAsync, upsertMessages, user, userId]);

  // Send media message
  const sendMediaMessage = useCallback(async (file, caption = '', type = 'image') => {
    if (!currentConversation) return null;

    const clientId = generateClientId();
    const localUrl = typeof URL !== 'undefined' && file ? URL.createObjectURL(file) : null;
    const optimistic = normalizeMessage(
      {
        id: clientId,
        clientId,
        pending: true,
        status: 'sending',
        conversationId: currentConversation.id,
        type,
        content: localUrl,
        metadata: {
          ...(caption ? { caption } : {}),
          ...(localUrl ? { url: localUrl } : {}),
        },
        sender: user ? { id: user.id, username: user.username, profile: user.profile } : undefined,
        recipients: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { conversationId: currentConversation.id },
    );

    setMessages((prev) => upsertMessages(prev, optimistic));

    try {
      const newMessage = await MessageService.sendMediaMessage(
        currentConversation.id,
        file,
        caption,
        type
      );
      
      // Use the server's URL instead of the local blob URL
      const messageToAdd = normalizeMessage(newMessage, {
        clientId,
        pending: false,
        status: 'sent',
        conversationId: currentConversation.id,
      });

      setMessages((prev) => {
        const withoutServerDup = messageToAdd?.id ? prev.filter((m) => m?.id !== messageToAdd.id) : prev;
        return upsertMessages(withoutServerDup, { ...messageToAdd, pending: false, clientId });
      });
      
      // Clean up the blob URL AFTER state is updated (with a small delay to ensure React has rendered)
      if (localUrl) {
        setTimeout(() => {
          URL.revokeObjectURL(localUrl);
        }, 100);
      }
      
      setConversations(prev => 
        prev.map(conv => 
          conv.id === currentConversation.id 
            ? { 
                ...conv, 
                lastMessage: messageToAdd,
                lastMessageAt: messageToAdd.createdAt || new Date().toISOString(),
                unreadCount: 0
              }
            : conv
        )
      );
      
      setCurrentConversation(prev => ({
        ...prev,
        lastMessage: messageToAdd,
        lastMessageAt: messageToAdd.createdAt || new Date().toISOString()
      }));

      safeAsync(async () => {
        await upsertMessage(userId, currentConversation.id, messageToAdd);
        await cacheConversation(userId, {
          ...(currentConversation || {}),
          lastMessage: messageToAdd,
          lastMessageAt: messageToAdd.createdAt || new Date().toISOString(),
          currentUserId: userId,
        });
      });
      
      return newMessage;
    } catch (err) {
      setError(err.message);
      setMessages((prev) =>
        prev.map((m) => (m?.clientId === clientId || m?.id === clientId ? { ...m, pending: false, status: 'failed' } : m)),
      );
      throw err;
    }
  }, [currentConversation, generateClientId, normalizeMessage, safeAsync, upsertMessages, user, userId]);

  // Send GIF message
  const sendGifMessage = useCallback(async (gifUrl, width, height, caption = '') => {
    if (!currentConversation) return null;

    const clientId = generateClientId();
    const optimistic = normalizeMessage(
      {
        id: clientId,
        clientId,
        pending: true,
        status: 'sending',
        conversationId: currentConversation.id,
        type: 'gif',
        content: gifUrl,
        metadata: { url: gifUrl, width, height, caption },
        sender: user ? { id: user.id, username: user.username, profile: user.profile } : undefined,
        recipients: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { conversationId: currentConversation.id },
    );

    setMessages((prev) => upsertMessages(prev, optimistic));

    try {
      const newMessage = await MessageService.sendGifMessage(
        currentConversation.id,
        gifUrl,
        width,
        height,
        caption
      );
      
      const messageToAdd = normalizeMessage(newMessage, {
        clientId,
        pending: false,
        status: 'sent',
        conversationId: currentConversation.id,
        type: 'gif',
        content: gifUrl,
        metadata: { url: gifUrl, width, height, caption },
        sender: user ? { id: user.id, username: user.username, profile: user.profile } : undefined,
      });

      setMessages((prev) => {
        const withoutServerDup = messageToAdd?.id ? prev.filter((m) => m?.id !== messageToAdd.id) : prev;
        return upsertMessages(withoutServerDup, { ...messageToAdd, pending: false, clientId });
      });
      
      setConversations(prev => 
        prev.map(conv => 
          conv.id === currentConversation.id 
            ? { 
                ...conv, 
                lastMessage: messageToAdd,
                lastMessageAt: messageToAdd.createdAt || new Date().toISOString(),
                unreadCount: 0
              }
            : conv
        )
      );
      
      setCurrentConversation(prev => ({
        ...prev,
        lastMessage: messageToAdd,
        lastMessageAt: messageToAdd.createdAt || new Date().toISOString()
      }));

      safeAsync(async () => {
        await upsertMessage(userId, currentConversation.id, messageToAdd);
        await cacheConversation(userId, {
          ...(currentConversation || {}),
          lastMessage: messageToAdd,
          lastMessageAt: messageToAdd.createdAt || new Date().toISOString(),
          currentUserId: userId,
        });
      });
      
      return newMessage;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [currentConversation, generateClientId, normalizeMessage, safeAsync, upsertMessages, user, userId]);

  // Mute conversation
  const muteConversation = useCallback(async (conversationId, duration = '1d') => {
    try {
      await MessageService.muteConversation(conversationId, duration);
      await fetchConversations();
    } catch (error) {
      console.error('Error muting conversation:', error);
      throw error;
    }
  }, [fetchConversations]);

  // Unmute conversation
  const unmuteConversation = useCallback(async (conversationId) => {
    try {
      await MessageService.unmuteConversation(conversationId);
      await fetchConversations();
    } catch (error) {
      console.error('Error unmuting conversation:', error);
      throw error;
    }
  }, [fetchConversations]);

  // Delete conversation
  const deleteConversation = useCallback(async (conversationId, deleteType = 'for_me') => {
    try {
      console.log('Deleting conversation:', conversationId);
      await MessageService.deleteConversation(conversationId, deleteType);
      console.log('Conversation deleted from server');
      
      // Clear from localStorage if it's the last selected conversation
      const lastConversationId = localStorage.getItem('messages:lastConversationId');
      if (lastConversationId === conversationId) {
        console.log('Clearing from localStorage');
        localStorage.removeItem('messages:lastConversationId');
      }
      
      // If deleting the currently selected conversation, clear it
      if (currentConversation?.id === conversationId) {
        console.log('Clearing currently selected conversation');
        setCurrentConversation(null);
        setMessages([]);
        setPinnedMessages([]);
      }
      
      // Remove from state
      setConversations(prev => {
        const filtered = prev.filter(c => c.id !== conversationId);
        console.log('Conversations after delete:', filtered.length);
        return filtered;
      });

      // Remove from cache immediately
      console.log('Removing from cache:', conversationId);
      await removeCachedConversation(userId, conversationId);
      
      // Update the cached conversations list
      const updatedConversations = conversationsRef.current.filter(c => c.id !== conversationId);
      console.log('Updating cached conversations list, count:', updatedConversations.length);
      await cacheConversations(userId, updatedConversations);
      console.log('Delete operation complete');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }, [userId, currentConversation]);

  // Get unread count for a conversation
  const getUnreadCount = useCallback((conversationId) => {
    return conversationUnreadCount[conversationId] || 0;
  }, [conversationUnreadCount]);

  const markConversationAsReadLocal = useCallback((conversationId) => {
    if (!conversationId) return;
    setConversationUnreadCount((prev) => ({
      ...(prev || {}),
      [conversationId]: 0,
    }));

    setConversations((prev) =>
      (Array.isArray(prev) ? prev : []).map((conv) => {
        if (conv?.id !== conversationId) return conv;
        return {
          ...conv,
          stats: {
            ...(conv?.stats || {}),
            unread: 0,
          },
        };
      }),
    );
  }, []);

  // Setup socket listeners
  useEffect(() => {
    if (!user || !messagesSocketService) return;

    const handleMessageReceived = (data) => {
      console.log('Message received:', data);
      console.log('[DEBUG] userId:', userId, '| sender.id:', data?.sender?.id);
      // Only update conversation list, don't refetch to avoid clearing current messages

      try {
        const isViewingConversation = Boolean(currentConversation?.id && data?.conversationId && currentConversation.id === data.conversationId);
        const recipients = Array.isArray(data?.recipients) ? data.recipients : [];
        const isFromMe = Boolean(userId && data?.sender?.id && data.sender.id === userId);
        console.log('[DEBUG] isFromMe:', isFromMe, '| isViewing:', isViewingConversation);

        // Primary: check recipients structure if available
        const getRecipientId = (r) => r?.recipientId || r?.id || r?.userId || r?.user?.id;
        const hasUnreadFromRecipients = Boolean(
          userId &&
          recipients.length > 0 &&
          recipients.some((r) => getRecipientId(r) === userId && !r?.readAt && r?.status !== 'seen')
        );

        // Fallback: use isRead flag when recipients structure is different
        const hasUnreadFromFlag = Boolean(
          !isFromMe && (data?.isRead === false || data?.isRead === undefined)
        );

        const hasUnreadForMe = hasUnreadFromRecipients || hasUnreadFromFlag;
        console.log('[DEBUG] hasUnreadFromRecipients:', hasUnreadFromRecipients, '| hasUnreadFromFlag:', hasUnreadFromFlag, '| hasUnreadForMe:', hasUnreadForMe);

        if (data?.conversationId && hasUnreadForMe && !isFromMe && !isViewingConversation) {
          console.log('[DEBUG] INCREMENTING UNREAD for', data.conversationId);
          
          // Play notification sound for new unread message (respects per-conversation setting)
          const convSoundEnabled = localStorage.getItem(`chat_notif_sound_${data.conversationId}`);
          const shouldPlaySound = convSoundEnabled === null ? true : convSoundEnabled === 'true';
          if (shouldPlaySound) {
            SoundManager.playTone();
          }
          
          setConversationUnreadCount((prev) => {
            const current = prev?.[data.conversationId] || 0;
            return {
              ...(prev || {}),
              [data.conversationId]: current + 1,
            };
          });

          // Keep conversations list unread stats in sync (used by popup bar + inbox)
          setConversations((prev) =>
            (Array.isArray(prev) ? prev : []).map((conv) => {
              if (conv?.id !== data.conversationId) return conv;
              return {
                ...conv,
                stats: {
                  ...(conv?.stats || {}),
                  unread: Number(conv?.stats?.unread || 0) + 1,
                },
              };
            }),
          );
        }
      } catch {
      }

      if (data?.conversationId) {
        const normalizedForList = normalizeMessage(data, { conversationId: data.conversationId });
        setConversations((prev) => {
          const base = Array.isArray(prev) ? prev : [];
          const nowIso = new Date().toISOString();
          const lastAt = normalizedForList?.createdAt || nowIso;

          const idx = base.findIndex((c) => c?.id === data.conversationId);
          if (idx >= 0) {
            const copy = base.slice();
            copy[idx] = {
              ...copy[idx],
              lastMessage: normalizedForList,
              lastMessageAt: lastAt,
              updatedAt: lastAt,
            };
            return copy;
          }

          // If we haven't loaded this conversation yet, socket payload often includes it.
          const socketConversation = data?.conversation;
          if (socketConversation && socketConversation?.id) {
            return [
              {
                ...socketConversation,
                currentUserId: userId,
                lastMessage: normalizedForList,
                lastMessageAt: lastAt,
                updatedAt: lastAt,
                stats: {
                  ...(socketConversation?.stats || {}),
                  unread: Number(socketConversation?.stats?.unread || 0),
                },
              },
              ...base,
            ];
          }

          return base;
        });

        safeAsync(async () => {
          await upsertMessage(userId, data.conversationId, normalizedForList);
          await cacheConversation(userId, {
            ...(conversationsRef.current.find((c) => c.id === data.conversationId) || { id: data.conversationId }),
            lastMessage: normalizedForList,
            lastMessageAt: lastAt,
            currentUserId: userId,
          });
        });
      }
      
      if (currentConversation && data.conversationId === currentConversation.id) {
        const normalized = normalizeMessage(data, { conversationId: currentConversation.id });
        setMessages((prev) => upsertMessages(prev, normalized));
      }
    };

    const handleMessageStatus = (data) => {
      if (!data?.conversationId || !data?.messageId || !data?.status) return;

      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== data.conversationId) return conv;
          const updatedAt = data.updatedAt || new Date().toISOString();
          return {
            ...conv,
            lastMessageAt: conv.lastMessageAt || updatedAt,
            updatedAt: conv.updatedAt || updatedAt,
          };
        }),
      );

      if (currentConversation && data.conversationId === currentConversation.id) {
        const statusRank = { sent: 0, delivered: 1, seen: 2 };
        const rankOf = (s) => statusRank[String(s || 'sent')] ?? 0;

        const getParticipantUser = (conversation, uid) => {
          if (!conversation || !uid) return null;
          const participant = (conversation.participants || []).find((p) => {
            const pid = p?.user?.id || p?.participantId || p?.id;
            return pid === uid;
          });
          const u = participant?.user;
          if (!u) return null;
          return {
            id: u.id,
            username: u.username,
            profile: {
              displayName: u?.profile?.displayName || null,
              avatar: getUserAvatarUrlFromConversation(conversation, uid) || u?.profile?.avatar || null,
            },
          };
        };

        setMessages((prev) => {
          const target = prev.find((m) => m?.id === data.messageId);
          const targetAt = target?.createdAt ? new Date(target.createdAt).getTime() : null;

          return prev.map((msg) => {
            if (!msg) return msg;
            // Only receipt updates for sender's messages
            if (msg?.sender?.id !== userId) return msg;

            const msgAt = msg.createdAt ? new Date(msg.createdAt).getTime() : null;
            const isUpToTarget = targetAt && msgAt ? msgAt <= targetAt : msg.id === data.messageId;
            if (!isUpToTarget) return msg;

            const existingRecipients = Array.isArray(msg.recipients) ? msg.recipients : [];
            const existing = existingRecipients.find((r) => r?.recipientId === data.recipientId);
            const current = existing?.status || 'sent';
            if (rankOf(data.status) <= rankOf(current)) {
              return msg;
            }

            const nextRecipients = existingRecipients.map((r) => {
              if (r?.recipientId !== data.recipientId) return r;
              return {
                ...r,
                status: data.status,
                readAt: data.status === 'seen' ? (r.readAt || data.updatedAt || new Date().toISOString()) : r.readAt,
                user: r.user || getParticipantUser(currentConversation, data.recipientId),
              };
            });

            const hasRecipient = existingRecipients.some((r) => r?.recipientId === data.recipientId);
            if (!hasRecipient && data.recipientId) {
              nextRecipients.push({
                recipientId: data.recipientId,
                recipientType: 'user',
                status: data.status,
                readAt: data.status === 'seen' ? (data.updatedAt || new Date().toISOString()) : null,
                user: getParticipantUser(currentConversation, data.recipientId),
              });
            }

            return {
              ...msg,
              recipients: nextRecipients,
              status: rankOf(data.status) > rankOf(msg.status) ? data.status : msg.status,
              updatedAt: data.updatedAt || msg.updatedAt,
            };
          });
        });
      }
    };

    const handleConversationUpdated = async (data) => {
      // Refetch conversations list
      fetchConversations();
      
      // If this is a nickname change and we're viewing this conversation, refresh it
      if (data?.action === 'nickname_changed' && data?.conversationId === currentConversation?.id) {
        try {
          const response = await MessageService.getConversation(data.conversationId);
          if (response?.data) {
            setCurrentConversation(prev => ({
              ...prev,
              participants: response.data.participants
            }));
          }
        } catch (error) {
          console.error('Error refreshing conversation after nickname change:', error);
        }
      }
    };

    const handleMessageReaction = (data) => {
      if (!data?.conversationId) return;

      const incomingMessageId = data?.id || data?.messageId;
      if (!incomingMessageId) return;

      const isDelta = Boolean(!data?.id && data?.messageId && data?.emoji);

      const normalizedReactions =
        Array.isArray(data?.reactions)
          ? data.reactions
          : Array.isArray(data?.metadata?.reactions)
            ? data.metadata.reactions
            : [];

      const applyDelta = (msg) => {
        const reactions = Array.isArray(msg?.reactions) ? msg.reactions : [];
        const actorId = data?.userId || data?.actorId;
        if (!actorId) return msg;
        const emoji = data?.emoji;
        const action = data?.action;

        if (!emoji) return msg;

        if (action === 'remove') {
          return {
            ...msg,
            reactions: reactions.filter(
              (r) => !((r?.userId || r?.user?.id) === actorId && (r?.emoji || r?.type) === emoji),
            ),
            updatedAt: data?.at || msg.updatedAt,
          };
        }

        const withoutUser = reactions.filter((r) => (r?.userId || r?.user?.id) !== actorId);
        return {
          ...msg,
          reactions: [...withoutUser, { userId: actorId, emoji, type: emoji, at: data?.at }],
          updatedAt: data?.at || msg.updatedAt,
        };
      };

      setMessages((prev) => {
        const updated = prev.map((msg) => {
          if (!msg || msg.id !== incomingMessageId) return msg;

          if (isDelta) {
            return applyDelta(msg);
          }

          return {
            ...msg,
            ...data,
            conversationId: data.conversationId,
            reactions: normalizedReactions,
            updatedAt: data.updatedAt || msg.updatedAt,
          };
        });

        const updatedMsg = updated.find((m) => m?.id === incomingMessageId);
        if (updatedMsg) {
          safeAsync(async () => {
            await upsertMessage(userId, data.conversationId, updatedMsg);
          });
        }

        return updated;
      });

      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== data.conversationId) return conv;
          const lastId = conv?.lastMessage?.id || conv?.lastMessage?.messageId;
          if (lastId !== incomingMessageId) return conv;

          if (isDelta) {
            const nextLast = conv?.lastMessage ? applyDelta(conv.lastMessage) : conv.lastMessage;
            return {
              ...conv,
              lastMessage: nextLast,
            };
          }

          return {
            ...conv,
            lastMessage: {
              ...(conv.lastMessage || {}),
              ...data,
              reactions: normalizedReactions,
            },
          };
        }),
      );

      if (currentConversation?.id === data.conversationId) {
        setCurrentConversation((prev) => {
          if (!prev) return prev;
          const prevMessages = Array.isArray(prev.messages) ? prev.messages : [];
          const nextMessages = prevMessages.map((m) =>
            m?.id === incomingMessageId
              ? isDelta
                ? applyDelta(m)
                : {
                    ...m,
                    ...data,
                    conversationId: data.conversationId,
                    reactions: normalizedReactions,
                    updatedAt: data.updatedAt || m.updatedAt,
                  }
              : m,
          );
          return {
            ...prev,
            messages: nextMessages,
          };
        });
      }
    };

    const handlePinnedMessage = (data) => {
      const conversationId = data?.conversationId;
      const event = data?.event;
      if (!conversationId) return;
      if (event !== 'message:pinned' && event !== 'message:unpinned') return;
      safeAsync(async () => {
        await refreshPinnedMessages(conversationId);
      });
    };

    const messageListener = messagesSocketService.addListener('onMessageReceived', handleMessageReceived);
    const updateListener = messagesSocketService.addListener('onConversationUpdated', handleConversationUpdated);
    const statusListener = messagesSocketService.addListener('onMessageStatus', handleMessageStatus);
    const reactionListener = messagesSocketService.addListener('onMessageReaction', handleMessageReaction);
    const pinnedListener = messagesSocketService.addListener('onConversationUpdated', handlePinnedMessage);

    return () => {
      messagesSocketService.removeListener('onMessageReceived', messageListener);
      messagesSocketService.removeListener('onConversationUpdated', updateListener);
      messagesSocketService.removeListener('onMessageStatus', statusListener);
      messagesSocketService.removeListener('onMessageReaction', reactionListener);
      messagesSocketService.removeListener('onConversationUpdated', pinnedListener);
    };
  }, [user, currentConversation, messagesSocketService, fetchConversations, getUserAvatarUrlFromConversation, normalizeMessage, refreshPinnedMessages, safeAsync, userId]);

  // Add reaction to message
  const addReaction = useCallback(async (messageId, emoji) => {
    if (!currentConversation) return;
    
    try {
      const existingMsg = messagesRef.current?.find?.((m) => m?.id === messageId);
      const existingReactions = existingMsg?.reactions || [];
      const alreadyReacted = existingReactions.some(
        (r) => (r?.userId || r?.user?.id) === user?.id && (r?.emoji || r?.type) === emoji,
      );
      const action = alreadyReacted ? 'remove' : 'add';

      if (messagesSocketService && currentConversation?.id) {
        safeAsync(async () => {
          await messagesSocketService.reactToMessage(currentConversation.id, messageId, emoji, action);
        });
      }

      await MessageService.addReaction(messageId, emoji, currentConversation.id);
      
      // Optimistically update local state
      setMessages(prevMessages => 
        (() => {
          const updated = prevMessages.map(msg => {
            if (msg.id === messageId) {
              const reactions = msg.reactions || [];
              const existingReaction = reactions.find(
                r => (r.userId || r.user?.id) === user?.id && (r.emoji || r.type) === emoji
              );
              
              if (!existingReaction) {
                return {
                  ...msg,
                  reactions: [...reactions, { userId: user?.id, emoji, type: emoji }]
                };
              }
            }
            return msg;
          });

          const updatedMsg = updated.find((m) => m.id === messageId);
          if (updatedMsg) {
            safeAsync(async () => {
              await upsertMessage(userId, currentConversation.id, updatedMsg);
            });
          }

          return updated;
        })()
      );
    } catch (error) {
      console.error('Failed to add reaction:', error);
      throw error;
    }
  }, [currentConversation, user, safeAsync, userId]);

  // Remove reaction from message
  const removeReaction = useCallback(async (messageId, emoji) => {
    if (!currentConversation) return;
    
    try {
      if (messagesSocketService && currentConversation?.id) {
        safeAsync(async () => {
          await messagesSocketService.reactToMessage(currentConversation.id, messageId, emoji, 'remove');
        });
      }

      await MessageService.removeReaction(messageId, emoji, currentConversation.id);
      
      // Optimistically update local state
      setMessages(prevMessages => 
        (() => {
          const updated = prevMessages.map(msg => {
            if (msg.id === messageId) {
              const reactions = msg.reactions || [];
              return {
                ...msg,
                reactions: reactions.filter(
                  r => !((r.userId || r.user?.id) === user?.id && (r.emoji || r.type) === emoji)
                )
              };
            }
            return msg;
          });

          const updatedMsg = updated.find((m) => m.id === messageId);
          if (updatedMsg) {
            safeAsync(async () => {
              await upsertMessage(userId, currentConversation.id, updatedMsg);
            });
          }

          return updated;
        })()
      );
    } catch (error) {
      console.error('Failed to remove reaction:', error);
      throw error;
    }
  }, [currentConversation, user, safeAsync, userId]);

  // Archive conversation
  const archiveConversation = useCallback(async (conversationId, archived = true) => {
    try {
      await MessageService.archiveConversation(conversationId, archived);
      
      // Update local state
      if (archived) {
        // Clear from localStorage if it's the last selected conversation
        const lastConversationId = localStorage.getItem('messages:lastConversationId');
        if (lastConversationId === conversationId) {
          localStorage.removeItem('messages:lastConversationId');
        }
        
        // If archiving the currently selected conversation, clear it
        if (currentConversation?.id === conversationId) {
          setCurrentConversation(null);
          setMessages([]);
          setPinnedMessages([]);
        }
      }
      
      // Refresh conversations list to get updated archive status
      await fetchConversations();
      
      if (!archived) {
        // When unarchiving, just mark it - it will be fetched on next refresh
        setConversations(prevConversations =>
          prevConversations.map(conv =>
            conv.id === conversationId ? { ...conv, archived } : conv
          )
        );
        
        // Update cache
        const existing = await getCachedConversation(userId, conversationId);
        await cacheConversation(userId, { ...(existing || { id: conversationId }), archived, currentUserId: userId });
      }
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      throw error;
    }
  }, [userId, currentConversation, fetchConversations]);

  // Initial fetch
  useEffect(() => {
    // Preload notification sounds
    SoundManager.preload('message', '/sounds/sound-chat.mp3');
    
    if (user) {
      fetchConversations();
    }
  }, [user, fetchConversations]);

  const value = {
    conversations,
    currentConversation,
    messages,
    pinnedMessages,
    loading,
    loadingMoreConversations,
    loadingOlderMessages,
    error,
    hasMore: hasMoreMessages,
    hasMoreMessages,
    messagesPage,
    hasMoreConversations,
    conversationsPage,
    totalUnreadCount,
    conversationUnreadCount,
    fetchConversations,
    fetchMoreConversations,
    selectConversation,
    fetchOlderMessages,
    refreshPinnedMessages,
    pinMessage,
    unpinMessage,
    sendMessage,
    sendMediaMessage,
    sendGifMessage,
    muteConversation,
    unmuteConversation,
    deleteConversation,
    archiveConversation,
    getUnreadCount,
    markConversationAsReadLocal,
    addReaction,
    removeReaction,
    messagesSocketService,
    user,
  };

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
};

export default MessagesContext;
